import React, { useState } from 'react';
import { Package2, Plus } from 'lucide-react';
import EmptyState from '../EmptyState';
import DeleteConfirmationModal from '../DeleteConfirmationModal';
import { BookingMaterial } from '../../lib/bookings';
import { Material } from '../../lib/materials';
import SelectMaterialModal from '../manage/SelectMaterialModal';
import { useBusiness } from '../../contexts/BusinessContext';

interface BookingMaterialsTabProps {
  materials: BookingMaterial[];
  onAddMaterial: (material: Material, quantity: number) => Promise<void>;
  onRemoveMaterial: (id: string) => Promise<void>;
  onUpdate: () => Promise<void>;
  onError: (error: string) => void;
  onOpenMaterialModal: () => void;
  booking: { status: string };
}

export default function BookingMaterialsTab({
  materials = [],
  onAddMaterial,
  onRemoveMaterial,
  onUpdate,
  onError,
  onOpenMaterialModal,
  booking
}: BookingMaterialsTabProps) {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [materialToDelete, setMaterialToDelete] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [isSelectMaterialModalOpen, setIsSelectMaterialModalOpen] = useState(false);
  const [permissionLevel, setPermissionLevel] = useState<'manage' | 'view' | 'disabled'>('manage');
  const { selectedBusiness } = useBusiness();

  const handleAddClick = () => {
    if (booking.status === 'completed') {
      onError('Cannot add materials to completed bookings');
      return;
    }
    setIsSelectMaterialModalOpen(true);
  };

  return (
    <>
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden min-h-[400px] flex flex-col">
      <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
        <h3 className="text-sm font-medium text-gray-900">Materials</h3>
        {materials.length > 0 && permissionLevel === 'manage' && (
          <button
            onClick={handleAddClick}
            className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium text-gray-700 bg-gray-50 border border-gray-300 rounded-md hover:bg-gray-100"
          >
            <Plus className="h-3.5 w-3.5 mr-1.5" />
            Add Material
          </button>
        )}
      </div>
      <div className="flex-1">
        {materials.length === 0 ? (
          <div className="flex items-center justify-center p-12">
            <EmptyState
              icon={<Package2 className="w-8 h-8 text-gray-400" />}
              title="No materials added"
              description="Track materials used for this booking"
              actionLabel={permissionLevel === 'manage' ? "Add Material" : "No materials added"}
              onAction={handleAddClick}
            />
          </div>
        ) : (
          <div className="space-y-4">
            {materials.map((material) => (
              <div key={material.id} className="bg-white rounded-lg p-3 border border-gray-200">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-gray-900">{material.material_name}</h3>
                    {material.material_description && (
                      <p className="mt-1 text-sm text-gray-500">{material.material_description}</p>
                    )}
                    <div className="mt-2 text-xs text-gray-500">
                      {Number(material.quantity).toFixed(2)} {material.unit} × ${Number(material.cost).toFixed(2)} = ${(Number(material.quantity) * Number(material.cost)).toFixed(2)}
                    </div>
                  </div>
                  {permissionLevel === 'manage' && (
                    <button
                      onClick={() => {
                        if (booking.status === 'completed') {
                          onError('Cannot remove materials from completed bookings');
                          return;
                        }
                        setMaterialToDelete(material.id);
                        setIsDeleteModalOpen(true);
                      }}
                      disabled={booking.status === 'completed'}
                      className={`p-1.5 rounded-md ${
                        booking.status === 'completed'
                          ? 'text-gray-300 cursor-not-allowed'
                          : 'text-red-400 hover:text-red-600 hover:bg-red-50'
                      }`}
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
    
    <DeleteConfirmationModal
      isOpen={isDeleteModalOpen}
      onClose={() => {
        setIsDeleteModalOpen(false);
        setMaterialToDelete(null);
      }}
      onConfirm={async () => {
        if (!materialToDelete) return;
        try {
          setDeleteLoading(true);
          await onRemoveMaterial(materialToDelete);
          setIsDeleteModalOpen(false);
          setMaterialToDelete(null);
        } finally {
          setDeleteLoading(false);
        }
      }}
      title="Remove Material"
      message="Are you sure you want to remove this material? This action cannot be undone."
      loading={deleteLoading}
    />
    <SelectMaterialModal
      isOpen={isSelectMaterialModalOpen}
      onClose={() => setIsSelectMaterialModalOpen(false)}
      onAdd={onAddMaterial}
      businessId={selectedBusiness?.id}
    />
    </>
  );
}